import type {
  GetRequestsResponse,
  SuperAdminCompletedRequest,
} from "@nlc/shared-types";
import type { SortDescriptor } from "react-stately";

import { RequestTypeEnum, SuperAdminTabEnum } from "@nlc/shared-types";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { requestListKeys } from "@/hooks/queryKeys";

import type { RequestFilterType } from "../../types/filters";

import { useSuperAdminRequestsByType } from "../superadmin/useSuperAdminRequestsByType";

type SuperAdminTab =
  | "completed"
  | "due-today"
  | "open"
  | "overdue"
  | "paused"
  | "queued"
  | "ready-for-review";

interface TableState {
  currentPage: number;
  itemsPerPage: number;
  sortDescriptor: SortDescriptor;
}

interface UseSuperAdminTabDataOptions {
  isActiveTab?: boolean;
  searchValue?: string;
  selectedFilter?: RequestFilterType;
  tab: SuperAdminTab;
  tableStates: Record<string, TableState>;
}

// Map tab names to SuperAdminTabEnum values
const TAB_ENUM_MAP: Record<SuperAdminTab, SuperAdminTabEnum> = {
  completed: SuperAdminTabEnum.COMPLETED,
  "due-today": SuperAdminTabEnum.DUE_TODAY,
  open: SuperAdminTabEnum.OPEN,
  overdue: SuperAdminTabEnum.OVERDUE,
  paused: SuperAdminTabEnum.PAUSED,
  queued: SuperAdminTabEnum.QUEUED,
  "ready-for-review": SuperAdminTabEnum.READY_FOR_REVIEW,
};

/**
 * Independent data management hook for SuperAdmin tabs
 * PERFORMANCE OPTIMIZED: Individual API calls per request type for accurate pagination
 * LAZY LOADING: Only fetches data when tab is active
 * Provides independent pagination, sorting, and filtering per request type
 */
export function useSuperAdminTabData({
  isActiveTab = true,
  searchValue,
  selectedFilter,
  tab,
  tableStates,
}: UseSuperAdminTabDataOptions) {
  const queryClient = useQueryClient();

  // Get table states for all request types
  const graphicTableState = tableStates[RequestTypeEnum.GRAPHIC];
  const videoTableState = tableStates[RequestTypeEnum.VIDEO];
  const webDesignTableState = tableStates[RequestTypeEnum.WEBSITE_DESIGN];
  const webImplTableState = tableStates[RequestTypeEnum.WEBSITE_IMPLEMENTATION];

  // Individual API calls for each request type - this provides accurate pagination per type
  // Only enabled when tab is active (removed tableState checks as they're always truthy)
  const graphicData = useSuperAdminRequestsByType({
    enabled: isActiveTab,
    filter: selectedFilter,
    limit: graphicTableState?.itemsPerPage || 10,
    page: graphicTableState?.currentPage || 1,
    requestType: RequestTypeEnum.GRAPHIC,
    search: searchValue,
    sortDescriptor: graphicTableState?.sortDescriptor || {
      column: "createdAt",
      direction: "descending",
    },
    tab: TAB_ENUM_MAP[tab],
  });

  const videoData = useSuperAdminRequestsByType({
    enabled: isActiveTab,
    filter: selectedFilter,
    limit: videoTableState?.itemsPerPage || 10,
    page: videoTableState?.currentPage || 1,
    requestType: RequestTypeEnum.VIDEO,
    search: searchValue,
    sortDescriptor: videoTableState?.sortDescriptor || {
      column: "createdAt",
      direction: "descending",
    },
    tab: TAB_ENUM_MAP[tab],
  });

  const webDesignData = useSuperAdminRequestsByType({
    enabled: isActiveTab,
    filter: selectedFilter,
    limit: webDesignTableState?.itemsPerPage || 10,
    page: webDesignTableState?.currentPage || 1,
    requestType: RequestTypeEnum.WEBSITE_DESIGN,
    search: searchValue,
    sortDescriptor: webDesignTableState?.sortDescriptor || {
      column: "createdAt",
      direction: "descending",
    },
    tab: TAB_ENUM_MAP[tab],
  });

  const webImplData = useSuperAdminRequestsByType({
    enabled: isActiveTab,
    filter: selectedFilter,
    limit: webImplTableState?.itemsPerPage || 10,
    page: webImplTableState?.currentPage || 1,
    requestType: RequestTypeEnum.WEBSITE_IMPLEMENTATION,
    search: searchValue,
    sortDescriptor: webImplTableState?.sortDescriptor || {
      column: "createdAt",
      direction: "descending",
    },
    tab: TAB_ENUM_MAP[tab],
  });

  // Create the request type data object with actual hook results
  const requestTypeData = useMemo(
    () => ({
      [RequestTypeEnum.GRAPHIC]: graphicData,
      [RequestTypeEnum.VIDEO]: videoData,
      [RequestTypeEnum.WEBSITE_DESIGN]: webDesignData,
      [RequestTypeEnum.WEBSITE_IMPLEMENTATION]: webImplData,
    }),
    [graphicData, videoData, webDesignData, webImplData],
  );

  // Optimistic update function for real-time updates across all tables
  const updateRequestOptimistically = useMemo(
    () => (requestId: number, updates: Partial<SuperAdminCompletedRequest>) => {
      // Update all individual query caches that might contain this request
      const requestTypes = [
        RequestTypeEnum.GRAPHIC,
        RequestTypeEnum.VIDEO,
        RequestTypeEnum.WEBSITE_DESIGN,
        RequestTypeEnum.WEBSITE_IMPLEMENTATION,
      ];

      for (const requestType of requestTypes) {
        const tableState = tableStates[requestType];

        if (!tableState) continue;

        const queryKey = [
          ...requestListKeys.all,
          "superadmin-requests-by-type",
          TAB_ENUM_MAP[tab],
          requestType,
          selectedFilter,
          searchValue,
          tableState.sortDescriptor.column || "createdAt",
          tableState.sortDescriptor.direction || "desc",
          tableState.currentPage,
          tableState.itemsPerPage,
        ];

        queryClient.setQueryData(
          queryKey,
          (oldData: GetRequestsResponse | undefined) => {
            if (!oldData?.requests) return oldData;

            return {
              ...oldData,
              requests: oldData.requests.map((request) =>
                request.id === requestId ? { ...request, ...updates } : request,
              ),
            };
          },
        );
      }
    },
    [queryClient, tab, selectedFilter, searchValue, tableStates],
  );

  // Cache invalidation function for real-time updates
  const invalidateTabData = useMemo(
    () => () => {
      queryClient.invalidateQueries({
        queryKey: [
          ...requestListKeys.all,
          "superadmin-requests-by-type",
          TAB_ENUM_MAP[tab],
        ],
      });
    },
    [queryClient, tab],
  );

  return {
    invalidateTabData,
    requestTypeData,
    updateRequestOptimistically,
  };
}
